import { useState } from "react";
import { Pencil, Plus, ShieldAlert, Trash2 } from "lucide-react";

import { Counted, ListCard, ListEmpty } from "@/shared/components/ListCard";
import { GhostButton, IconButton } from "@/shared/components/ui-kit";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent } from "@/shared/types";

/** The service's limits (backend/modules/agents/schemas/agents.py). */
const MAX_RULES = 50;
const MAX_CHARS = 500;

/** One line, as the service keeps a rule. */
const tidy = (text: string) => text.split(/\s+/).filter(Boolean).join(" ");

/* Rules the agent never breaks. A draft field like the instructions: each change
   goes to the draft at once, and runs read the rules once they are published. */
export default function GuardrailsSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const rules = agent.guardrails ?? [];
  const [adding, setAdding] = useState(false);
  const [newRule, setNewRule] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const full = rules.length >= MAX_RULES;

  const save = (next: string[]) => onUpdateAgent((a) => ({ ...a, guardrails: [...new Set(next.filter(Boolean))] }));
  const add = () => {
    const rule = tidy(newRule);
    setNewRule("");
    setAdding(false);
    if (rule) save([...rules, rule]);
  };
  const saveEdit = (at: number) => {
    setEditing(null);
    const rule = tidy(draft);
    if (rule !== rules[at]) save(rules.map((r, i) => (i === at ? rule : r)));
  };

  const field = "w-full bg-transparent text-[13.5px] leading-[1.55] text-[var(--text-primary)] outline-none resize-none tracking-[-0.005em] placeholder:text-[var(--text-quaternary)]";
  const ruleInput = (value: string, onChange: (v: string) => void, done: () => void, cancel: () => void, label: string) => (
    <textarea
      autoFocus
      rows={2}
      maxLength={MAX_CHARS}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); done(); }
        if (e.key === "Escape") cancel();
      }}
      onBlur={done}
      placeholder="A rule it must never break — Enter to save"
      aria-label={label}
      className={field}
    />
  );

  return (
    <Section section="guardrails" onHelp={onHelp}>
      <ListCard
        label={<Counted n={rules.length} noun="rule" />}
        action={
          <GhostButton
            onClick={() => setAdding(true)}
            disabled={full}
            title={full ? `An agent has at most ${MAX_RULES} rules` : undefined}
            className="h-7 px-2.5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add rule
          </GhostButton>
        }
      >
        {rules.length === 0 && !adding && (
          <ListEmpty icon={ShieldAlert} title="No guardrails" hint="Add the rules this agent must never break, like “Never promise a refund.” It reads them last, before every reply." />
        )}
        {rules.map((rule, i) => (
          <div key={rule} className="group flex items-start gap-3 px-4 py-3 min-w-0 hover:bg-[var(--accent-hover)]">
            <span className="shrink-0 w-5 text-[12px] leading-[21px] font-mono tabular-nums text-[var(--text-quaternary)]">{i + 1}</span>
            {editing === i ? (
              ruleInput(draft, setDraft, () => saveEdit(i), () => setEditing(null), `Edit rule ${i + 1}`)
            ) : (
              <>
                <span className="flex-1 min-w-0 text-[13.5px] leading-[1.55] text-[var(--text-primary)] tracking-[-0.005em] break-words">{rule}</span>
                <span className="shrink-0 flex lg:hidden lg:group-hover:flex lg:group-has-[:focus-visible]:flex items-center gap-0.5 h-[21px]">
                  <IconButton onClick={() => { setEditing(i); setDraft(rule); }} title="Edit" aria-label={`Edit rule ${i + 1}`}>
                    <Pencil className="w-3 h-3" strokeWidth={1.75} />
                  </IconButton>
                  <IconButton onClick={() => save(rules.filter((_, j) => j !== i))} title="Delete" aria-label={`Delete rule ${i + 1}`} className="hover:text-[var(--danger)]">
                    <Trash2 className="w-3 h-3" strokeWidth={1.75} />
                  </IconButton>
                </span>
              </>
            )}
          </div>
        ))}
        {adding && (
          <div className="flex items-start gap-3 px-4 py-3 fade-in">
            <span className="shrink-0 w-5 text-[12px] leading-[21px] font-mono tabular-nums text-[var(--text-quaternary)]">{rules.length + 1}</span>
            {ruleInput(newRule, setNewRule, add, () => { setNewRule(""); setAdding(false); }, "New rule")}
          </div>
        )}
      </ListCard>
    </Section>
  );
}
